import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';

import {BenchlingQueryResult, QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import useSelectorsById, {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableOutsideNotebookWithTableViews} from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import createOutputTable from 'processes/tables/createOutputTable';
import createTaskTable, {
  CreateTaskTableSchemaWithoutStatusLifecycleParams,
} from 'processes/tables/createTaskTable';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import {Header2} from 'ui-library/Headers';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  RequestV2SubmissionTasksAndOutputsQuery,
  RequestV2SubmissionTasksAndOutputsQueryVariables,
} from './__generated/RequestV2SubmissionTasksAndOutputsQuery';
import {RequestV2SubmissionDetails} from './RequestV2SubmissionDetailsQueryUtils';
import {REQUEST_V2_SUBMISSION_DETAILS_STYLES} from './RequestV2SubmissionDetailsStyles';

type ProcessGql = ExtractByTypename<RequestV2SubmissionTasksAndOutputsQuery['process'], 'ProcessNode'>;
type TaskSchemaGql = ExtractByTypename<ProcessGql['taskSchemaOrInaccessible'], 'ProcessTaskSchemaNode'>;
type ProcessWithAccessibleTaskSchema = ProcessGql & {taskSchemaOrInaccessible: TaskSchemaGql};

export const REQUEST_V2_SUBMISSION_TASKS_AND_OUTPUTS = gql`
  query RequestV2SubmissionTasksAndOutputsQuery($processId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    process(id: $processId) {
      ... on ProcessNode {
        id
        name
        taskSchemaOrInaccessible {
          ... on ProcessTaskSchemaNode {
            id
            name
            executionType
            fields
            allFields
            outputSchema {
              id
              name
              fields
              allFields
              processTaskSchemaName
              processExecutionType
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        tasks {
          id
          humanId
          status {
            id
            statusType
            displayName
          }
          assignee {
            ... on UserNode {
              id
              name
              handle
              avatarUrl
            }
          }
          scheduledOnDate
          fields
          archiveRecord {
            purpose
          }
          studies {
            ... on StudyNode {
              id
              name
              humanId
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
        }
        outputs {
          id
          humanId
          task {
            id
            humanId
          }
          fields
          archiveRecord {
            purpose
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function RequestV2TaskSection({
  process,
  selectorsById,
}: {
  process: ProcessWithAccessibleTaskSchema;
  selectorsById: SchemaFieldSelectorsDict;
}): JSX.Element {
  const taskTable =
    process.tasks.length > 0
      ? createTaskTable({
          process: {
            ...process,
            processTaskSchema:
              process.taskSchemaOrInaccessible as CreateTaskTableSchemaWithoutStatusLifecycleParams,
          },
          tableLocation: ProcessTableLocation.REQUEST_V2_SUBMISSION_DETAILS_PAGE,
          selectorsById,
        })
      : null;

  return (
    <div>
      <Header2 className={css(REQUEST_V2_SUBMISSION_DETAILS_STYLES.sectionHeader)}>Tasks</Header2>
      {taskTable != null ? (
        <StructuredTableOutsideNotebookWithTableViews
          cid={taskTable.cid}
          locked={true}
          structuredTable={taskTable}
          updateHandler={() => {}}
          enableFormulas={false}
          isFixedHeight={true}
        />
      ) : (
        <div className={css(styles.infoText)}>Tasks will appear here once they are created.</div>
      )}
    </div>
  );
}

function RequestV2OutputsSection({
  process,
  selectorsById,
}: {
  process: ProcessWithAccessibleTaskSchema;
  selectorsById: SchemaFieldSelectorsDict;
}): JSX.Element | null {
  if (process.taskSchemaOrInaccessible.outputSchema == null) {
    return null;
  }

  const outputTable =
    process.outputs.length > 0
      ? createOutputTable({
          process: {
            ...process,
            processTaskSchema: {
              ...process.taskSchemaOrInaccessible,
              outputSchema: process.taskSchemaOrInaccessible.outputSchema,
            },
          },
          metadata: {tableLocation: ProcessTableLocation.REQUEST_V2_SUBMISSION_DETAILS_PAGE},
          selectorsById,
        })
      : null;

  return (
    <div className={css(styles.outputTableSectionWrapper)}>
      <Header2 className={css(REQUEST_V2_SUBMISSION_DETAILS_STYLES.sectionHeader)}>Outputs</Header2>
      {outputTable != null ? (
        <StructuredTableOutsideNotebookWithTableViews
          cid={outputTable.cid}
          locked={true}
          structuredTable={outputTable}
          updateHandler={() => {}}
          enableFormulas={false}
          isFixedHeight={true}
        />
      ) : (
        <div className={css(styles.infoText)}>Outputs will appear here once they are created.</div>
      )}
    </div>
  );
}

function QueryingRequestV2SubmissionFulfillmentTables(props: {
  processId: string;
  onQueryEmitted: (
    query: BenchlingQueryResult<
      RequestV2SubmissionTasksAndOutputsQuery,
      RequestV2SubmissionTasksAndOutputsQueryVariables
    >
  ) => void;
}): JSX.Element {
  const selectorsById = useSelectorsById({includeArchivedSelectors: false});
  const result = useBenchlingQuery<
    RequestV2SubmissionTasksAndOutputsQuery,
    RequestV2SubmissionTasksAndOutputsQueryVariables
  >(REQUEST_V2_SUBMISSION_TASKS_AND_OUTPUTS, {
    variables: {
      processId: props.processId,
    },
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  useEffect(() => {
    props.onQueryEmitted(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  if (result.type === QueryResultType.ERROR || selectorsById === AsyncStatus.ERROR) {
    return <div />;
  } else if (
    selectorsById == null ||
    selectorsById === AsyncStatus.LOADING ||
    result.type === QueryResultType.PENDING
  ) {
    return <CenteredHorizontalThrobber />;
  }

  const process = result.data.process;
  if (isInaccessible(process) || isInaccessible(process.taskSchemaOrInaccessible)) {
    return <div className={css(styles.infoText)}>No access</div>;
  }

  return (
    <>
      <RequestV2OutputsSection
        process={process as ProcessWithAccessibleTaskSchema}
        selectorsById={selectorsById}
      />
      <RequestV2TaskSection
        process={process as ProcessWithAccessibleTaskSchema}
        selectorsById={selectorsById}
      />
    </>
  );
}

interface RequestV2SubmissionFulfillmentTablesProps {
  requestV2Submission: RequestV2SubmissionDetails;
  onQueryEmitted: (
    query: BenchlingQueryResult<
      RequestV2SubmissionTasksAndOutputsQuery,
      RequestV2SubmissionTasksAndOutputsQueryVariables
    >
  ) => void;
}

export default function RequestV2SubmissionFulfillmentTables(
  props: RequestV2SubmissionFulfillmentTablesProps
): JSX.Element {
  if (props.requestV2Submission.workflowTaskGroup == null) {
    return <div />;
  } else if (isInaccessible(props.requestV2Submission.workflowTaskGroup)) {
    return <div className={css(styles.wrapper, styles.infoText)}>No access</div>;
  }

  return (
    <div className={css(styles.wrapper)}>
      <QueryingRequestV2SubmissionFulfillmentTables
        processId={props.requestV2Submission.workflowTaskGroup.id}
        onQueryEmitted={props.onQueryEmitted}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    margin: 16,
  },
  outputTableSectionWrapper: {
    marginBottom: 24,
  },
  infoText: {
    color: GRAY_SEMIDARK,
  },
});
